import React, { useState } from 'react';
import ToastContainer from '../components/Toast/ToastContainer';
import { ToastService } from '../components/Toast/toastService';

const ToastDemo = () => {
  const [demonstratingToasts, setDemonstratingToasts] = useState(false);

  const triggerGlobalRefresh = () => {
    ToastService.triggerGlobalRefresh();
  };

  const clearDismissedToasts = () => {
    ToastService.clearDismissedToasts();
    window.location.reload(); // Reload to show all toasts again
  };

  const startDemo = () => {
    setDemonstratingToasts(true);
    // Clear dismissed toasts to ensure they all show
    ToastService.clearDismissedToasts();
    triggerGlobalRefresh();
  };

  return (
    <div id="main-container">
      <div id="content-outer-wrapper" className="flex flex-expand">
        <div className="flex" style={{ width: "100%" }}>
          <div id="content-wrapper" className="full-height-container">
            <section className="section-main no-pad-bottom">
              <section className="section-scroll-fade-top"></section>
              <div className="section-scroll" style={{ background: "#eeeff2" }}>
                <section className="form-section">
                  <h1>Toast Notification System Demo</h1>
                  
                  <div style={{ 
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <h2>System Overview</h2>
                    <p>The toast notification system provides a flexible, JSON-configured way to display user notifications without modifying code. This demo showcases all the different toast types and features.</p>
                    
                    <h3>Features Demonstrated:</h3>
                    <ul>
                      <li>✅ <strong>Multiple Toast Types:</strong> Info, Success, Warning, Error</li>
                      <li>✅ <strong>Dismissible Toasts:</strong> User can click "OK" to dismiss</li>
                      <li>✅ <strong>Auto-hide Toasts:</strong> Automatically disappear after a set duration</li>
                      <li>✅ <strong>Screen Targeting:</strong> Toasts only show on specified screens</li>
                      <li>✅ <strong>Persistent Dismissals:</strong> Dismissed toasts stay dismissed using localStorage</li>
                      <li>✅ <strong>Runtime Configuration:</strong> All settings controlled by external JSON file</li>
                      <li>✅ <strong>Expiration Handling:</strong> Toasts automatically expire after specified dates</li>
                    </ul>
                  </div>

                  <div style={{ 
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <h2>Demo Controls</h2>
                    <p>Use these buttons to demonstrate the toast system functionality:</p>
                    
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
                      <button 
                        onClick={startDemo}
                        className="btn btn-primary"
                        style={{ padding: '10px 20px' }}
                      >
                        🎯 Show All Toasts
                      </button>
                      
                      <button 
                        onClick={triggerGlobalRefresh}
                        className="btn btn-secondary"
                        style={{ padding: '10px 20px' }}
                      >
                        🔄 Refresh Toasts
                      </button>
                      
                      <button 
                        onClick={clearDismissedToasts}
                        className="btn btn-warning"
                        style={{ padding: '10px 20px' }}
                      >
                        🗑️ Reset Dismissed Toasts
                      </button>
                    </div>

                    <div style={{ 
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '5px',
                      border: '1px solid #dee2e6'
                    }}>
                      <h4>Instructions:</h4>
                      <ol>
                        <li><strong>Click "Show All Toasts"</strong> to display all configured toasts for this demo page</li>
                        <li><strong>Try dismissing toasts</strong> using the "OK" button on dismissible toasts</li>
                        <li><strong>Wait and observe</strong> auto-hide toasts that disappear automatically</li>
                        <li><strong>Use "Reset Dismissed Toasts"</strong> to clear localStorage and show all toasts again</li>
                        <li><strong>Check the positioning</strong> - toasts appear at bottom-center to avoid UI conflicts</li>
                      </ol>
                    </div>
                  </div>

                  <div style={{ 
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <h2>Configuration</h2>
                    <p>All toast messages are configured in <code>public/toasts.json</code>. Here's the configuration for this demo:</p>
                    
                    <pre style={{ 
                      backgroundColor: '#f8f9fa',
                      padding: '15px',
                      borderRadius: '5px',
                      overflow: 'auto',
                      fontSize: '12px'
                    }}>
{`{
  "toasts": [
    {
      "id": "the-system-works",
      "message": "Welcome to Integration Manager!",
      "screens": ["Home"],
      "type": "info",
      "duration": 5000,
      "dismissible": true
    },
    {
      "id": "success",
      "message": "This was a triumph!",
      "screens": ["Home"],
      "type": "success",
      "duration": 5000,
      "dismissible": true
    },
    {
      "id": "extra-message",
      "message": "This is superfluous!",
      "screens": ["Home"],
      "type": "warning",
      "duration": 5000,
      "dismissible": true
    },
    {
      "id": "something-wrong",
      "message": "Everything is fine and nothing is broken!",
      "screens": ["Home"],
      "type": "error",
      "duration": 5000,
      "dismissible": true
    }
  ]
}`}</pre>
                  </div>

                  <div style={{ 
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <h2>Technical Details</h2>
                    <h3>Components:</h3>
                    <ul>
                      <li><strong>ToastService:</strong> Handles fetching, filtering, and managing toast state</li>
                      <li><strong>ToastContainer:</strong> Manages display lifecycle and positioning of multiple toasts</li>
                      <li><strong>Toast:</strong> Individual toast component with type-based styling and interactions</li>
                    </ul>
                    
                    <h3>Key Features:</h3>
                    <ul>
                      <li><strong>Cache Busting:</strong> Prevents stale configurations with timestamp parameters</li>
                      <li><strong>Error Handling:</strong> Graceful degradation when toast configuration fails to load</li>
                      <li><strong>Memory Management:</strong> Proper cleanup of timers and event listeners</li>
                      <li><strong>Responsive Design:</strong> Fixed positioning that works across different screen sizes</li>
                    </ul>
                  </div>

                  {demonstratingToasts && (
                    <div style={{ 
                      backgroundColor: '#e7f3ff',
                      padding: '15px',
                      borderRadius: '5px',
                      border: '1px solid #0066cc',
                      marginBottom: '20px'
                    }}>
                      <h4>🎯 Demo Active!</h4>
                      <p>Look at the bottom-center of your screen to see the toast notifications. Each toast type has a different appearance and behavior.</p>
                    </div>
                  )}
                  
                  {/* Toast Container - This is where the magic happens */}
                  <ToastContainer screenName="Home" />
                </section>
              </div>
              <section className="section-scroll-fade-bottom"></section>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToastDemo;